import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { Activity, ArrowDown, Search, Trash2 } from 'lucide-react';
import { api, eventSource } from './api';
import { Select } from './Select';
import type { LogEntry, Notify } from './types';

type ConnectionState = 'connecting' | 'live' | 'offline';

type Props = {
  /** 历史接口，例如 /api/devices/{id}/logs 或 /api/logger。 */
  apiBase: string;
  /** 全局日志页：额外支持 deviceId 过滤，并在每行显示设备。 */
  global?: boolean;
  deviceId?: string;
  showDevice?: boolean;
  /** 工具栏最左侧的附加筛选（全局页的设备选择器）。 */
  leading?: ReactNode;
  notify: Notify;
};

const MAX_ENTRIES = 5000;
const PAGE_SIZE = 200;
const STATUS_LABEL: Record<ConnectionState, string> = {
  connecting: '连接中…',
  live: '实时连接',
  offline: '连接断开',
};

const errorText = (cause: unknown): string => (cause instanceof Error ? cause.message : '操作失败');

function formatLogTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('zh-CN', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

function hasMetadata(value: unknown): boolean {
  return Boolean(value) && typeof value === 'object' && Object.keys(value as Record<string, unknown>).length > 0;
}

/** Parse a `datetime-local` value in the browser's time zone. */
function parseLocalTime(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * LogStream 负责日志页的公共部分：历史加载、筛选、SSE 增量与终端渲染。
 *
 * 服务端对实时流采用“至少一次”投递（重叠窗口内可能重复），这里统一按
 * 日志行 id 去重；历史先到、实时后到时也不会重复。
 */
export function LogStream({ apiBase, global = false, deviceId = '', showDevice = false, leading, notify }: Props) {
  const [messages, setMessages] = useState<LogEntry[]>([]);
  const [status, setStatus] = useState<ConnectionState>('connecting');
  const [level, setLevel] = useState('');
  const [sourceInput, setSourceInput] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [timeError, setTimeError] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [follow, setFollow] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);
  const generationRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSource(sourceInput.trim());
      setSearch(searchInput.trim());
      const fromDate = parseLocalTime(fromInput);
      const toDate = parseLocalTime(toInput);
      if ((fromInput && !fromDate) || (toInput && !toDate)) {
        setTimeError('时间格式无效');
        return;
      }
      if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
        setTimeError('开始时间不能晚于结束时间');
        return;
      }
      setTimeError('');
      setFrom(fromDate ? fromDate.toISOString() : '');
      setTo(toDate ? toDate.toISOString() : '');
    }, 350);
    return () => window.clearTimeout(timer);
  }, [sourceInput, searchInput, fromInput, toInput]);

  const merge = useCallback((incoming: LogEntry[], position: 'prepend' | 'append') => {
    setMessages((current) => {
      const known = new Set(current.map((item) => item.id));
      const added = incoming.filter((item) => !known.has(item.id));
      if (!added.length) return current;
      const next = position === 'prepend' ? [...added, ...current] : [...current, ...added];
      return next.length > MAX_ENTRIES ? next.slice(next.length - MAX_ENTRIES) : next;
    });
  }, []);

  const buildFilterParams = useCallback((limit?: number) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    if (level) params.set('level', level);
    if (source) params.set('source', source);
    if (search) params.set('search', search);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (global && deviceId) params.set('device_id', deviceId);
    return params;
  }, [level, source, search, from, to, global, deviceId]);

  useEffect(() => {
    const generation = ++generationRef.current;
    let disposed = false;
    const controller = new AbortController();
    let stream: EventSource | null = null;
    setMessages([]);
    setNextCursor(null);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    setFollow(true);
    setStatus('connecting');
    setError('');

    const params = buildFilterParams(PAGE_SIZE);

    const loadHistory = async () => {
      const data = await api<{ items: LogEntry[]; nextCursor: string | null }>(`${apiBase}?${params}`, { signal: controller.signal });
      if (disposed || generation !== generationRef.current) return;
      merge(data.items, 'prepend');
      setNextCursor(data.nextCursor);
    };
    void loadHistory().catch((cause) => {
      if (disposed || (cause instanceof DOMException && cause.name === 'AbortError')) return;
      setStatus('offline');
      notify('error', errorText(cause));
    });

    const streamParams = buildFilterParams();
    const query = streamParams.toString();
    stream = eventSource(`${apiBase}/stream${query ? `?${query}` : ''}`);

    stream.onopen = () => {
      if (!disposed) {
        setStatus('live');
        setError('');
      }
    };
    stream.addEventListener('log', (event) => {
      if (disposed) return;
      try {
        const entry = JSON.parse((event as MessageEvent<string>).data) as LogEntry;
        if (entry && typeof entry.id === 'string') merge([entry], 'append');
      } catch {
        // 忽略格式错误的事件帧，保持连接继续。
      }
    });
    stream.addEventListener('expired', (event) => {
      if (disposed) return;
      setStatus('offline');
      try {
        const payload = JSON.parse((event as MessageEvent<string>).data) as { message?: string };
        if (payload.message) setError(payload.message);
      } catch {
        setError('会话已失效，请重新登录');
      }
      stream?.close();
    });
    stream.addEventListener('stream-error', (event) => {
      if (disposed) return;
      setStatus('connecting');
      try {
        const payload = JSON.parse((event as MessageEvent<string>).data) as { message?: string };
        if (payload.message) setError(payload.message);
      } catch {
        setError('日志流暂时不可用');
      }
    });
    stream.onerror = () => {
      if (disposed) return;
      setStatus(stream?.readyState === EventSource.CLOSED ? 'offline' : 'connecting');
    };

    return () => {
      disposed = true;
      controller.abort();
      stream?.close();
    };
  }, [apiBase, buildFilterParams, merge, notify]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMoreRef.current || messages.length >= MAX_ENTRIES - PAGE_SIZE) return;
    const node = scrollRef.current;
    if (!node) return;
    const generation = generationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const params = buildFilterParams(PAGE_SIZE);
    params.set('before', nextCursor);
    try {
      const data = await api<{ items: LogEntry[]; nextCursor: string | null }>(`${apiBase}?${params}`);
      if (generation !== generationRef.current) return;
      const previousHeight = node.scrollHeight;
      flushSync(() => merge(data.items, 'prepend'));
      node.scrollTop += node.scrollHeight - previousHeight;
      setNextCursor(data.nextCursor);
    } catch (cause) {
      if (generation === generationRef.current) notify('error', errorText(cause));
    } finally {
      if (generation === generationRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [apiBase, buildFilterParams, merge, messages.length, nextCursor, notify]);

  useEffect(() => {
    if (!follow) return;
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages.length, follow]);

  function onScroll() {
    const node = scrollRef.current;
    if (!node) return;
    setFollow(node.scrollHeight - node.scrollTop - node.clientHeight < 48);
    if (node.scrollTop < 64 && nextCursor && !loadingMoreRef.current) void loadMore();
  }

  return (
    <section className="log-panel" aria-label="实时日志">
      <div className="log-toolbar">
        {leading}
        <label className="log-filter">级别
          <Select aria-label="日志级别" value={level} onChange={(event) => setLevel(event.target.value)}>
            <option value="">全部级别</option>
            <option value="debug">debug</option>
            <option value="info">info</option>
            <option value="warn">warn</option>
            <option value="error">error</option>
          </Select>
        </label>
        <label className="log-filter">开始时间
          <input
            type="datetime-local"
            aria-label="日志开始时间"
            value={fromInput}
            max={toInput || undefined}
            onChange={(event) => setFromInput(event.target.value)}
          />
        </label>
        <label className="log-filter">结束时间
          <input
            type="datetime-local"
            aria-label="日志结束时间"
            value={toInput}
            min={fromInput || undefined}
            onChange={(event) => setToInput(event.target.value)}
          />
        </label>
        {(fromInput || toInput) && (
          <button
            type="button"
            className="text-button log-time-clear"
            onClick={() => { setFromInput(''); setToInput(''); setTimeError(''); }}
          >
            清除时间
          </button>
        )}
        <label className="log-filter">来源
          <input aria-label="日志来源" value={sourceInput} onChange={(event) => setSourceInput(event.target.value)} placeholder="watchdog" />
        </label>
        <label className="log-filter grow">搜索
          <span className="search-box compact">
            <Search size={15} aria-hidden="true" />
            <input aria-label="搜索日志" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="消息、来源或设备" />
          </span>
        </label>
        <span className={`log-status ${status}`} role="status"><i />{STATUS_LABEL[status]}</span>
      </div>

      {timeError && <p className="form-error" role="alert">{timeError}</p>}
      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="log-terminal">
        <header className="log-terminal-head">
          <span>
            {global ? '全部设备' : '当前设备'} · 已加载 {messages.length} 条
            {nextCursor ? ' · 向上滚动加载更早' : ''} · 实时增量 · IP 为服务端观察地址
          </span>
          <div>
            {!follow && <button type="button" className="text-button" onClick={() => setFollow(true)}><ArrowDown size={13} />回到最新</button>}
            <button type="button" className="text-button" onClick={() => { setMessages([]); setNextCursor(null); }}><Trash2 size={13} />清空</button>
          </div>
        </header>
        <div className="log-lines" ref={scrollRef} onScroll={onScroll} role="log" aria-live="polite">
          {loadingMore && <div className="log-loading" role="status">正在加载更早日志…</div>}
          {!loadingMore && !nextCursor && messages.length >= PAGE_SIZE && <div className="log-loading">已到最早</div>}
          {messages.map((entry) => (
            <div className={`log-line${showDevice ? ' with-device' : ''}`} key={entry.id}>
              <time dateTime={entry.ts} title={new Date(entry.ts).toLocaleString('zh-CN')}>{formatLogTime(entry.ts)}</time>
              <span className={`log-level ${entry.level}`}>{entry.level.toUpperCase()}</span>
              <span className="log-source" title={entry.source}>{entry.source}</span>
              {showDevice && <span className="log-device" title={entry.device_fqdn ?? entry.device_name}>{entry.device_name}{entry.device_fqdn ? ` · ${entry.device_fqdn}` : ''}</span>}
              <span
                className={`log-ip${entry.client_ip ? '' : ' empty'}`}
                title={entry.client_ip ? `服务端观察到的 IP：${entry.client_ip}` : '服务端未记录来源 IP（迁移前的历史日志）'}
              >{entry.client_ip ?? '—'}</span>
              <p className="log-message">{entry.message}</p>
              {hasMetadata(entry.metadata) && <code className="log-json">{JSON.stringify(entry.metadata)}</code>}
            </div>
          ))}
          {!messages.length && (
            <div className="log-empty">
              <Activity size={28} />
              <strong>{status === 'live' ? '已连接，等待日志' : '暂无日志'}</strong>
              <span>设备使用拥有 logger:write 权限的 key 上报后，日志会实时出现在这里。</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
